<template>
  <DatePicker
    color="gray"
    v-model="date"
    mode="date"
    ref="datePickerRef"
    class="w-full"
  >
    <template #default="{ togglePopover }">
      <div
        class="flex items-center justify-between cursor-pointer w-full bg-gray-100 rounded-full border border-gray-400 text-blue-600 text-xs md:text-sm font-medium px-3.5 py-1 gap-x-2"
        @click="togglePopover"
      >
        <span class="truncate">{{ formattedDate }}</span>
        <ChevronDownIcon
          class="text-gray-500 h-4 w-4 shrink-0"
          aria-hidden="true"
        />
      </div>
    </template>

    <template #footer>
      <div class="w-full px-4 pb-3 flex flex-col gap-2">
        <div class="flex gap-2">
          <button
            class="btn btn-secondary-outline w-full"
            @click="setToday"
          >
            Today
          </button>
          <button
            class="btn btn-secondary-outline w-full"
            @click="setTomorrow"
          >
            Tomorrow
          </button>
        </div>
        <button
          class="btn btn-secondary-outline underline"
          @click="clearDate"
        >
          Reset
        </button>
      </div>
    </template>
  </DatePicker>
</template>

<script setup>
import { computed, ref } from 'vue'
import { DatePicker } from 'v-calendar'
import { DateTime } from 'luxon'
import { onClickOutside } from '@vueuse/core'
import { ChevronDownIcon } from '@heroicons/vue/solid'
import { isToday, isTomorrow } from '@/utils/datetime'

const props = defineProps({
  modelValue: {
    type: [Date, null],
    default: null
  }
})

const emit = defineEmits(['update:modelValue'])

const date = computed({
  get: () => props.modelValue,
  set: (value) => {
    if (value?.getTime() !== props.modelValue?.getTime()) {
      emit('update:modelValue', value)
    }
  }
})

const datePickerRef = ref(null)

const formattedDate = computed(() => {
  if (!date.value) {
    return 'Any time'
  }

  if (isToday(date.value)) {
    return 'Today'
  } else if (isTomorrow(date.value)) {
    return 'Tomorrow'
  } else {
    return DateTime.fromJSDate(date.value).toFormat('MM/dd/yyyy')
  }
})

const setToday = () => {
  const today = DateTime.now().startOf('day').toJSDate()
  date.value = today
  datePickerRef.value.hidePopover()
}

const setTomorrow = () => {
  const tomorrow = DateTime.now().plus({ days: 1 }).startOf('day').toJSDate()
  date.value = tomorrow
  datePickerRef.value.hidePopover()
}

const clearDate = () => {
  date.value = null
  datePickerRef.value.hidePopover()
}
</script>
